// DEPLOY: /var/www/woody.wtf/frontend/src/chart/drawings.js
// woody.wtf chart engine: drawing tools + the editor that creates, selects and moves them.
// A drawing is {key, id?, type, pts: [{d, off, p}], style, text?, locked?, fib?}
//   style = {color, width, dash, lineOpacity 0-1, fillOpacity 0-1, priceTag? (hline/hray/fib, default on)}
//   fib   = {levels: [{v, on, color|null, fill}]}  (fill = shade band up to the next shown level)
//   alert = {dir: 'above'|'below', trigger: 'close'|'touch', after: 'once'|'rearm'|'daily', note, armed, rev}
//           (alert lines, checked at end of day by woody-jobs alert_lines.py; state = its result, read-only)
// (point format: see ChartEngine "Drawing anchors"). Each tool below defines how many
// points it takes, how it paints, and where it can be grabbed.
//
// Creating a two-point tool: press A and drag to B, or tap/click A then tap/click B
// (between the taps the chart still pans and pinches; on desktop B follows the mouse).
// Selecting: click/tap a drawing; Ctrl/Cmd/Shift+click (or the phone "Select several"
// mode) adds/removes drawings, and edits, drags and deletes then apply to all of them.
import { priceDp } from './ChartEngine';

export const FIB_DEFAULT = [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1];
const DASH = { solid: [], dash: [7, 5], dot: [2, 4] };
export const HAS_FILL = new Set(['rect', 'circle', 'ellipse', 'channel', 'text', 'fib', 'measure', 'callout', 'bubble']);   // tools with a shaded area (Fill slider)
const MARKERS = new Set(['buy', 'sell']);
const NO_FILL = new Set(['circle', 'ellipse']);              // start fully transparent until a fill is chosen
let drawingClipboard = null;
export const hasDrawingClipboard = () => !!drawingClipboard?.items.length;
let seq = 0;
const newKey = () => `k${Date.now().toString(36)}${(seq++).toString(36)}`;

const lineA = d => d.style.lineOpacity ?? 1;
const fillA = d => d.style.fillOpacity ?? 0.1;
export const fibLevels = d => d.fib?.levels || FIB_DEFAULT.map(v => ({ v, on: true, color: null, fill: false }));

function distToSeg(px, py, a, b, ray = false) {
  const dx = b.x - a.x, dy = b.y - a.y, l2 = dx * dx + dy * dy;
  let t = l2 ? ((px - a.x) * dx + (py - a.y) * dy) / l2 : 0;
  t = ray ? Math.max(0, t) : Math.max(0, Math.min(1, t));
  return Math.hypot(px - (a.x + t * dx), py - (a.y + t * dy));
}

const inBox = (p, a, b, pad) =>
  p.x >= Math.min(a.x, b.x) - pad && p.x <= Math.max(a.x, b.x) + pad &&
  p.y >= Math.min(a.y, b.y) - pad && p.y <= Math.max(a.y, b.y) + pad;

export function hexAlpha(hex, a) {
  const h = (hex || '#000').replace('#', '');
  const n = parseInt(h.length === 3 ? h.split('').map(c => c + c).join('') : h, 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}

function tag(g, x, y, text, bg, align = 'left') {
  g.font = "11px 'DM Mono', monospace";
  const w = g.measureText(text).width + 10;
  const left = align === 'center' ? x - w / 2 : align === 'right' ? x - w : x;
  g.fillStyle = bg; g.fillRect(left, y - 9, w, 18);
  g.fillStyle = '#fff'; g.textAlign = 'left'; g.textBaseline = 'middle';
  g.fillText(text, left + 5, y);
}

// ctx: { eng, r (price range), dp, P: pt -> {x,y}, plotR, bottom, theme, g }
// paint() runs with strokeStyle / lineWidth / dash / globalAlpha (line opacity) already set.
export const ALERT_DEFAULT = { dir: 'above', trigger: 'close', after: 'once', note: '', armed: true, rev: 0 };
export const ALERT_COLOUR = '#F59E0B';

// Alert line value on the last candle (adjusted), counted in daily candles like the EOD job; null outside A..B.
export function alertValueNow(d, eng) {
  const D = eng.daily;
  if (!D?.length || d.pts.length < 2) return null;
  const idx = pt => eng.dailyIdx(pt.d) + (pt.off || 0);
  let [a, b] = d.pts, ia = idx(a), ib = idx(b);
  if (ib < ia) { [a, b] = [b, a]; [ia, ib] = [ib, ia]; }
  const k = D.length - 1;
  if (ib === ia || k < ia || k > ib) return null;
  const va = a.p * eng.ptFactor(a), vb = b.p * eng.ptFactor(b);
  return va + (vb - va) * (k - ia) / (ib - ia);
}

// Arc bulge point when none is set yet: off the middle of A->B by a quarter of its length,
// on the upper side of the line.
export function arcDefaultH(a, b) {
  const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2, len = Math.hypot(b.x - a.x, b.y - a.y) || 1;
  let nx = -(b.y - a.y) / len, ny = (b.x - a.x) / len;
  if (ny > 0) { nx = -nx; ny = -ny; }
  return { x: mx + nx * len * 0.25, y: my + ny * len * 0.25 };
}

export const alertStatus = d => (d.alert?.armed === false ? 'off' : d.state?.status || 'new');


const CALLOUTS = new Set(['callout', 'bubble']);
const CALL_WIDTH = 176, CALL_HEIGHT = 44, CALL_LINE = 18;
const clampTo = (v, low, high) => Math.max(low, Math.min(high, v));

function calloutBox(g, d, c) {
  const tip = c.P(d.pts[0]), pos = c.P(d.pts[1]);
  const w = Math.max(92, d.style.boxWidth || CALL_WIDTH), maxText = w - 20;
  g.font = "13px 'DM Sans', sans-serif";
  const lines = [];
  for (const paragraph of (d.text || '').split('\n')) {
    let line = '';
    for (const word of paragraph.split(/(\s+)/)) {
      if (g.measureText(line + word).width <= maxText) { line += word; continue; }
      if (line.trim()) { lines.push(line.trimEnd()); line = ''; }
      if (!word.trim()) continue;
      for (const char of word) {
        if (line && g.measureText(line + char).width > maxText) { lines.push(line); line = ''; }
        line += char;
      }
    }
    lines.push(line.trimEnd());
  }
  return { x: pos.x, y: pos.y, w, h: Math.max(CALL_HEIGHT, d.style.boxHeight || 0, lines.length * CALL_LINE + 16), lines, tip };
}

function calloutEdge(b) {
  const dx = b.tip.x - b.x - b.w / 2, dy = b.tip.y - b.y - b.h / 2;
  const side = Math.abs(dx / b.w) > Math.abs(dy / b.h)
    ? (dx >= 0 ? 'right' : 'left') : (dy >= 0 ? 'bottom' : 'top');
  const horizontal = side === 'top' || side === 'bottom';
  const at = horizontal
    ? clampTo(b.tip.x, b.x + 18, b.x + b.w - 18)
    : clampTo(b.tip.y, b.y + 18, b.y + b.h - 18);
  const point = horizontal
    ? { x: at, y: side === 'top' ? b.y : b.y + b.h }
    : { x: side === 'left' ? b.x : b.x + b.w, y: at };
  return { side, at, point };
}

function calloutPath(g, b, edge, bubble) {
  const { x, y, w, h, tip } = b, r = 8, span = 9, e = edge;
  const tail = side => bubble && e.side === side;
  g.beginPath(); g.moveTo(x + r, y);
  if (tail('top')) { g.lineTo(e.at - span, y); g.lineTo(tip.x, tip.y); g.lineTo(e.at + span, y); }
  g.lineTo(x + w - r, y); g.quadraticCurveTo(x + w, y, x + w, y + r);
  if (tail('right')) { g.lineTo(x + w, e.at - span); g.lineTo(tip.x, tip.y); g.lineTo(x + w, e.at + span); }
  g.lineTo(x + w, y + h - r); g.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  if (tail('bottom')) { g.lineTo(e.at + span, y + h); g.lineTo(tip.x, tip.y); g.lineTo(e.at - span, y + h); }
  g.lineTo(x + r, y + h); g.quadraticCurveTo(x, y + h, x, y + h - r);
  if (tail('left')) { g.lineTo(x, e.at + span); g.lineTo(tip.x, tip.y); g.lineTo(x, e.at - span); }
  g.lineTo(x, y + r); g.quadraticCurveTo(x, y, x + r, y);
  g.closePath();
}

function paintCallout(g, d, c, bubble) {
  const b = calloutBox(g, d, c), e = calloutEdge(b);
  const arrow = d.style.color;
  const inside = inBox(b.tip, { x: b.x, y: b.y }, { x: b.x + b.w, y: b.y + b.h }, 0);
  if (!bubble && !inside) {
    g.strokeStyle = arrow;
    g.beginPath(); g.moveTo(e.point.x, e.point.y); g.lineTo(b.tip.x, b.tip.y); g.stroke();
    const a = Math.atan2(b.tip.y - e.point.y, b.tip.x - e.point.x), cs = Math.cos(a), sn = Math.sin(a);
    g.beginPath(); g.moveTo(b.tip.x, b.tip.y);
    g.lineTo(b.tip.x - 11 * cs + 5 * sn, b.tip.y - 11 * sn - 5 * cs);
    g.lineTo(b.tip.x - 11 * cs - 5 * sn, b.tip.y - 11 * sn + 5 * cs);
    g.closePath(); g.fillStyle = arrow; g.fill();
  }
  calloutPath(g, b, e, bubble && !inside);
  g.save(); g.globalAlpha = 1;
  g.fillStyle = hexAlpha(d.style.backgroundColor || c.theme.bg, fillA(d)); g.fill();
  g.restore();
  g.strokeStyle = d.style.color; g.stroke();
  if (bubble && !inside) {
    const s = 9, p = e.point, side = e.side;
    const start = side === 'left' || side === 'right' ? { x: p.x, y: p.y - s } : { x: p.x - s, y: p.y };
    const end = side === 'left' || side === 'right' ? { x: p.x, y: p.y + s } : { x: p.x + s, y: p.y };
    g.strokeStyle = arrow; g.beginPath(); g.moveTo(start.x, start.y);
    g.lineTo(b.tip.x, b.tip.y); g.lineTo(end.x, end.y); g.stroke();
  }
  g.save(); g.globalAlpha = 1; g.font = "13px 'DM Sans', sans-serif";
  g.fillStyle = d.style.textColor || c.theme.text;
  g.textAlign = 'left'; g.textBaseline = 'middle';
  b.lines.forEach((line, i) => g.fillText(line, b.x + 10, b.y + 8 + CALL_LINE * (i + 0.5)));
  g.restore();
}


function markerPoints(d, c, buy) {
  const tip = c.P(d.pts[0]), size = 0.6 * Math.max(0.75, Math.min(1.5, 1 + ((d.style.width ?? 2) - 2) * 0.25));
  const dir = buy ? 1 : -1;  // Arrow tip is anchored to the clicked price; body extends away.
  return [[0, 0], [20, 18], [9, 18], [9, 44], [-9, 44], [-9, 18], [-20, 18]]
    .map(([x, y]) => ({ x: tip.x + x * size, y: tip.y + y * size * dir }));
}

function paintMarker(g, d, c, buy) {
  const [tip, ...rest] = markerPoints(d, c, buy);
  g.beginPath(); g.moveTo(tip.x, tip.y);
  for (const pt of rest) g.lineTo(pt.x, pt.y);
  g.closePath(); g.fillStyle = d.style.color; g.fill();
}

function hitMarker(d, c, p, tol, buy) {
  const pts = markerPoints(d, c, buy);
  const x = pts.map(pt => pt.x), y = pts.map(pt => pt.y);
  return inBox(p, { x: Math.min(...x), y: Math.min(...y) }, { x: Math.max(...x), y: Math.max(...y) }, tol);
}

export const TOOLS = {
  alert: {
    label: 'Alert line', n: 2,
    // A to B only (no extension). Faded when off / fired / expired; a small triangle shows the
    // direction (up above the line / down below it); dot where it last fired.
    paint(g, d, c) {
      const [a, b] = d.pts.map(c.P), st = alertStatus(d), live = st === 'new' || st === 'armed';
      g.save();
      if (!live) { g.globalAlpha *= 0.45; g.setLineDash([6, 4]); }
      g.beginPath(); g.moveTo(a.x, a.y); g.lineTo(b.x, b.y); g.stroke();
      g.restore();
      // Direction marker only (details live in the alert settings): a small triangle at the
      // middle of the line, pointing up above it for "above", down below it for "below".
      const up = (d.alert || ALERT_DEFAULT).dir !== 'below';
      const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2, k = up ? -1 : 1, sz = 5;
      g.save(); g.globalAlpha = live ? 0.9 : 0.4; g.fillStyle = d.style.color; g.beginPath();
      g.moveTo(mx, my + k * (6 + sz * 1.6)); g.lineTo(mx - sz, my + k * 6); g.lineTo(mx + sz, my + k * 6); g.closePath(); g.fill();
      g.restore();
      if (d.state?.last_fired_d) {
        const fx = c.eng.xOf(c.eng.ptIndex({ d: d.state.last_fired_d, off: 0 }));
        const t = (fx - a.x) / ((b.x - a.x) || 1);
        if (t >= -0.01 && t <= 1.01) {
          g.save(); g.fillStyle = d.style.color; g.beginPath(); g.arc(fx, a.y + (b.y - a.y) * t, 5, 0, Math.PI * 2); g.fill();
          g.strokeStyle = '#fff'; g.lineWidth = 1.5; g.stroke(); g.restore();
        }
      }
    },
    hit(d, c, p, tol) { const [a, b] = d.pts.map(c.P); return distToSeg(p.x, p.y, a, b) <= tol; },
  },
  arc: {
    label: 'Arc', n: 2,
    // A smooth curve from A to B that passes through a third point H (its handle in the
    // middle sets how far and which way it bows). Drawn as a quadratic curve whose control
    // point puts the curve's midpoint exactly on H. H is added when the drawing is finished;
    // while A->B is being drawn it bows by a quarter of its length.
    curve(d, c) {
      const [a, b] = d.pts.map(c.P);
      const h = d.pts[2] ? c.P(d.pts[2]) : arcDefaultH(a, b);
      return { a, b, ctl: { x: 2 * h.x - (a.x + b.x) / 2, y: 2 * h.y - (a.y + b.y) / 2 } };
    },
    paint(g, d, c) {
      const { a, b, ctl } = this.curve(d, c);
      g.beginPath(); g.moveTo(a.x, a.y); g.quadraticCurveTo(ctl.x, ctl.y, b.x, b.y); g.stroke();
    },
    hit(d, c, p, tol) {
      const { a, b, ctl } = this.curve(d, c), pt = t => ({
        x: (1 - t) ** 2 * a.x + 2 * (1 - t) * t * ctl.x + t * t * b.x,
        y: (1 - t) ** 2 * a.y + 2 * (1 - t) * t * ctl.y + t * t * b.y });
      for (let i = 0; i < 24; i++) if (distToSeg(p.x, p.y, pt(i / 24), pt((i + 1) / 24)) <= tol) return true;
      return false;
    },
  },
  trend: {
    label: 'Trend line', n: 2,
    paint(g, d, c) { const [a, b] = d.pts.map(c.P); g.beginPath(); g.moveTo(a.x, a.y); g.lineTo(b.x, b.y); g.stroke(); },
    hit(d, c, p, tol) { const [a, b] = d.pts.map(c.P); return distToSeg(p.x, p.y, a, b) <= tol; },
  },
  channel: {
    label: 'Parallel channel', n: 3,
    edges(d, c) {
      const [a, b, h] = d.pts.map(c.P), dx = b.x - a.x;
      const off = Math.abs(dx) >= 1
        ? { x: 0, y: h.y - a.y - (b.y - a.y) * (h.x - a.x) / dx }
        : { x: h.x - a.x, y: 0 };
      return { a, b, u: { x: a.x + off.x, y: a.y + off.y },
               v: { x: b.x + off.x, y: b.y + off.y } };
    },
    paint(g, d, c) {
      const { a, b, u, v } = this.edges(d, c);
      g.save(); g.globalAlpha = 1; g.fillStyle = hexAlpha(d.style.color, fillA(d));
      g.beginPath(); g.moveTo(a.x, a.y); g.lineTo(b.x, b.y);
      g.lineTo(v.x, v.y); g.lineTo(u.x, u.y); g.closePath(); g.fill(); g.restore();
      g.beginPath(); g.moveTo(a.x, a.y); g.lineTo(b.x, b.y);
      g.moveTo(u.x, u.y); g.lineTo(v.x, v.y); g.stroke();
      g.save(); g.globalAlpha = d.style.midOpacity ?? 1; g.setLineDash([2, 4]);
      g.beginPath(); g.moveTo((a.x + u.x) / 2, (a.y + u.y) / 2);
      g.lineTo((b.x + v.x) / 2, (b.y + v.y) / 2); g.stroke(); g.restore();
    },
    hit(d, c, p, tol) {
      const { a, b, u, v } = this.edges(d, c);
      return distToSeg(p.x, p.y, a, b) <= tol || distToSeg(p.x, p.y, u, v) <= tol
        || (d.style.midOpacity !== 0 && distToSeg(p.x, p.y,
             { x: (a.x + u.x) / 2, y: (a.y + u.y) / 2 },
             { x: (b.x + v.x) / 2, y: (b.y + v.y) / 2 }) <= tol);
    },
  },
  buy: {
    label: 'Buy arrow', n: 1,
    paint(g, d, c) { paintMarker(g, d, c, true); },
    hit(d, c, p, tol) { return hitMarker(d, c, p, tol, true); },
  },
  sell: {
    label: 'Sell arrow', n: 1,
    paint(g, d, c) { paintMarker(g, d, c, false); },
    hit(d, c, p, tol) { return hitMarker(d, c, p, tol, false); },
  },
  arrow: {
    label: 'Arrow', n: 2,
    paint(g, d, c) {
      const [a, b] = d.pts.map(c.P), dx = b.x - a.x, dy = b.y - a.y;
      const len = Math.hypot(dx, dy);
      if (len < 1) return;
      const ux = dx / len, uy = dy / len, head = Math.min(11, len / 2), wing = head * 0.5;
      g.beginPath(); g.moveTo(a.x, a.y); g.lineTo(b.x, b.y); g.stroke();
      g.beginPath(); g.moveTo(b.x, b.y);
      g.lineTo(b.x - head * ux + wing * uy, b.y - head * uy - wing * ux);
      g.lineTo(b.x - head * ux - wing * uy, b.y - head * uy + wing * ux);
      g.closePath(); g.fillStyle = d.style.color; g.fill();
    },
    hit(d, c, p, tol) { const [a, b] = d.pts.map(c.P); return distToSeg(p.x, p.y, a, b) <= tol; },
  },
  ray: {
    label: 'Ray', n: 2,
    // Starts at A, passes through B and runs on to the edge of the chart.
    paint(g, d, c) {
      const [a, b] = d.pts.map(c.P), len = Math.hypot(b.x - a.x, b.y - a.y) || 1, k = 20000 / len;
      g.beginPath(); g.moveTo(a.x, a.y); g.lineTo(a.x + (b.x - a.x) * k, a.y + (b.y - a.y) * k); g.stroke();
    },
    hit(d, c, p, tol) { const [a, b] = d.pts.map(c.P); return distToSeg(p.x, p.y, a, b, true) <= tol; },
  },
  hline: {
    label: 'Horizontal line', n: 1,
    paint(g, d, c) {
      const a = c.P(d.pts[0]);
      g.beginPath(); g.moveTo(0, a.y); g.lineTo(c.plotR, a.y); g.stroke();
      if (d.style.priceTag !== false) tag(g, c.plotR - 2, a.y, (d.pts[0].p * c.factor(d.pts[0])).toFixed(c.dp), d.style.color, 'right');
    },
    hit(d, c, p, tol) { return Math.abs(p.y - c.P(d.pts[0]).y) <= tol; },
  },
  hray: {
    label: 'Horizontal ray', n: 1,
    // From the point to the right edge of the chart; price tag unless switched off.
    paint(g, d, c) {
      const a = c.P(d.pts[0]);
      g.beginPath(); g.moveTo(a.x, a.y); g.lineTo(c.plotR, a.y); g.stroke();
      if (d.style.priceTag !== false) tag(g, c.plotR - 2, a.y, (d.pts[0].p * c.factor(d.pts[0])).toFixed(c.dp), d.style.color, 'right');
    },
    hit(d, c, p, tol) { const a = c.P(d.pts[0]); return p.x >= a.x - tol && Math.abs(p.y - a.y) <= tol; },
  },
  vline: {
    label: 'Vertical line', n: 1,
    paint(g, d, c) { const a = c.P(d.pts[0]); g.beginPath(); g.moveTo(a.x, 0); g.lineTo(a.x, c.bottom); g.stroke(); },
    hit(d, c, p, tol) { return Math.abs(p.x - c.P(d.pts[0]).x) <= tol; },
  },
  rect: {
    label: 'Rectangle', n: 2,
    paint(g, d, c) {
      const [a, b] = d.pts.map(c.P), x = Math.min(a.x, b.x), y = Math.min(a.y, b.y), w = Math.abs(b.x - a.x), h = Math.abs(b.y - a.y);
      g.save(); g.globalAlpha = 1; g.fillStyle = hexAlpha(d.style.color, fillA(d)); g.fillRect(x, y, w, h); g.restore();
      g.strokeRect(x, y, w, h);
    },
    hit(d, c, p, tol) { const [a, b] = d.pts.map(c.P); return inBox(p, a, b, tol); },
  },
  circle: {
    label: 'Circle', n: 2,
    // Centre A, edge B. Radius is the on-screen distance A->B, so it always stays round.
    paint(g, d, c) {
      const [a, b] = d.pts.map(c.P), r = Math.hypot(b.x - a.x, b.y - a.y);
      g.beginPath(); g.arc(a.x, a.y, r, 0, Math.PI * 2);
      g.save(); g.globalAlpha = 1; g.fillStyle = hexAlpha(d.style.color, fillA(d)); g.fill(); g.restore();
      g.stroke();
    },
    // Outline only when unfilled, so the chart can still be panned from inside it.
    hit(d, c, p, tol) {
      const [a, b] = d.pts.map(c.P), r = Math.hypot(b.x - a.x, b.y - a.y), dist = Math.hypot(p.x - a.x, p.y - a.y);
      return fillA(d) > 0 ? dist <= r + tol : Math.abs(dist - r) <= tol;
    },
  },
  ellipse: {
    label: 'Ellipse', n: 2,
    // Fits the A->B box (anchored to price/candles like the rectangle, so it stretches with the view).
    paint(g, d, c) {
      const [a, b] = d.pts.map(c.P);
      g.beginPath(); g.ellipse((a.x + b.x) / 2, (a.y + b.y) / 2, Math.abs(b.x - a.x) / 2, Math.abs(b.y - a.y) / 2, 0, 0, Math.PI * 2);
      g.save(); g.globalAlpha = 1; g.fillStyle = hexAlpha(d.style.color, fillA(d)); g.fill(); g.restore();
      g.stroke();
    },
    hit(d, c, p, tol) {
      const [a, b] = d.pts.map(c.P), cx = (a.x + b.x) / 2, cy = (a.y + b.y) / 2;
      const rx = Math.max(Math.abs(b.x - a.x) / 2, 1), ry = Math.max(Math.abs(b.y - a.y) / 2, 1);
      const dx = p.x - cx, dy = p.y - cy, dist = Math.hypot(dx, dy);
      const edge = dist ? 1 / Math.hypot(dx / dist / rx, dy / dist / ry) : Math.min(rx, ry);   // centre-to-outline along this direction
      return fillA(d) > 0 ? dist <= edge + tol : Math.abs(dist - edge) <= tol;
    },
  },
  fib: {
    label: 'Fib retracement', n: 2,
    // Level 0 at B (end of the move), 1 at A (its start). Shown levels sorted by value.
    levels(d, c) {
      const [pa, pb] = d.pts.map(pt => pt.p * c.factor(pt));
      return fibLevels(d).filter(l => l.on).sort((x, y) => x.v - y.v)
        .map(l => ({ ...l, price: pb + (pa - pb) * l.v }));
    },
    paint(g, d, c) {
      const [a, b] = d.pts.map(c.P), x1 = Math.min(a.x, b.x), x2 = Math.max(a.x, b.x);
      const lv = this.levels(d, c);
      g.save(); g.globalAlpha = 1;                    // bands: fill opacity only
      lv.forEach((l, i) => {
        const nx = lv[i + 1];
        if (!l.fill || !nx) return;
        const y1 = c.Y(l.price), y2 = c.Y(nx.price);
        g.fillStyle = hexAlpha(l.color || d.style.color, fillA(d));
        g.fillRect(x1, Math.min(y1, y2), x2 - x1, Math.abs(y2 - y1));
      });
      g.restore();
      g.save(); g.setLineDash([3, 3]); g.globalAlpha *= 0.6;
      g.beginPath(); g.moveTo(a.x, a.y); g.lineTo(b.x, b.y); g.stroke(); g.restore();
      g.font = "11px 'DM Mono', monospace"; g.textBaseline = 'bottom'; g.textAlign = 'left';
      for (const l of lv) {
        const y = c.Y(l.price), col = l.color || d.style.color;
        g.strokeStyle = col;
        g.beginPath(); g.moveTo(x1, y); g.lineTo(x2, y); g.stroke();
        g.fillStyle = col;
        g.fillText(d.style.priceTag === false ? `${l.v}` : `${l.v} (${l.price.toFixed(c.dp)})`, x1 + 3, y - 2);
      }
    },
    hit(d, c, p, tol) {
      const [a, b] = d.pts.map(c.P);
      if (distToSeg(p.x, p.y, a, b) <= tol) return true;
      if (p.x < Math.min(a.x, b.x) - tol || p.x > Math.max(a.x, b.x) + tol) return false;
      return this.levels(d, c).some(({ price }) => Math.abs(c.Y(price) - p.y) <= tol);
    },
  },
  measure: {
    label: 'Measure', n: 2,
    // Shaded A->B box (green up / red down), arrow from A's price to B's, label with
    // price change, % change and candles between the points.
    paint(g, d, c) {
      const [a, b] = d.pts.map(c.P);
      const [pa, pb] = d.pts.map(pt => pt.p * c.factor(pt));
      const up = pb >= pa, col = up ? c.theme.up : c.theme.down;
      const x1 = Math.min(a.x, b.x), x2 = Math.max(a.x, b.x), mx = (a.x + b.x) / 2;
      g.save(); g.globalAlpha = 1;
      g.fillStyle = hexAlpha(col, fillA(d));
      g.fillRect(x1, Math.min(a.y, b.y), x2 - x1, Math.abs(b.y - a.y));
      g.restore();
      g.save(); g.strokeStyle = col; g.lineWidth = 1.5; g.setLineDash([]);
      g.beginPath(); g.moveTo(mx, a.y); g.lineTo(mx, b.y);
      const hd = up ? 6 : -6;
      g.moveTo(mx - 5, b.y + hd); g.lineTo(mx, b.y); g.lineTo(mx + 5, b.y + hd);
      g.stroke(); g.restore();
      const diff = pb - pa, pct = pa ? diff / Math.abs(pa) * 100 : 0;
      const bars = Math.round(Math.abs(c.eng.ptIndex(d.pts[1]) - c.eng.ptIndex(d.pts[0])));
      const sign = diff >= 0 ? '+' : '';
      const ly = up ? Math.min(a.y, b.y) - 14 : Math.max(a.y, b.y) + 14;
      const parts = [
        d.style.measurePrice !== false && `${sign}${diff.toFixed(c.dp)}`,
        d.style.measurePercent !== false && `${sign}${pct.toFixed(2)}%`,
        d.style.measureCandles !== false && `${bars} bars`,
      ].filter(Boolean);
      if (parts.length) {
        const label = parts.join(' · ');
        g.save();
        g.globalAlpha = 1; g.font = "10px 'DM Mono', monospace";
        const w = g.measureText(label).width;
        const x = Math.max(w / 2 + 3, Math.min(mx, c.plotR - w / 2 - 3));
        g.textAlign = 'center'; g.textBaseline = 'middle';
        g.strokeStyle = c.theme.bg; g.lineWidth = 3; g.setLineDash([]);
        g.strokeText(label, x, ly);
        g.fillStyle = col; g.fillText(label, x, ly);
        g.restore();
      }
    },
    hit(d, c, p, tol) { const [a, b] = d.pts.map(c.P); return inBox(p, a, b, tol); },
  },
  callout: {
    label: 'Arrow callout', n: 2,
    box: calloutBox,
    paint(g, d, c) { paintCallout(g, d, c, false); },
    hit(d, c, p, tol) {
      const b = calloutBox(c.g, d, c), e = calloutEdge(b);
      return inBox(p, { x: b.x, y: b.y }, { x: b.x + b.w, y: b.y + b.h }, tol)
        || distToSeg(p.x, p.y, b.tip, e.point) <= tol;
    },
  },
  bubble: {
    label: 'Speech bubble', n: 2,
    box: calloutBox,
    paint(g, d, c) { paintCallout(g, d, c, true); },
    hit(d, c, p, tol) {
      const b = calloutBox(c.g, d, c), e = calloutEdge(b);
      return inBox(p, { x: b.x, y: b.y }, { x: b.x + b.w, y: b.y + b.h }, tol)
        || distToSeg(p.x, p.y, b.tip, e.point) <= tol;
    },
  },
  text: {
    label: 'Text', n: 1,
    LINE: 18,
    box(g, d, c) {
      const a = c.P(d.pts[0]), lines = (d.text || '').split('\n');
      g.font = "13px 'DM Sans', sans-serif";
      const w = Math.max(...lines.map(s => g.measureText(s).width)) + 12, h = lines.length * this.LINE + 6;
      return { x: a.x, y: a.y - 11, w, h, lines };
    },
    paint(g, d, c) {
      const bx = this.box(g, d, c);
      g.save(); g.globalAlpha = 1; g.fillStyle = hexAlpha(d.style.color, fillA(d)); g.fillRect(bx.x, bx.y, bx.w, bx.h); g.restore();
      g.fillStyle = d.style.color; g.textAlign = 'left'; g.textBaseline = 'middle';
      bx.lines.forEach((s, i) => g.fillText(s, bx.x + 6, bx.y + 3 + this.LINE * (i + 0.5)));
    },
    hit(d, c, p, tol) { const bx = this.box(c.g, d, c); return inBox(p, { x: bx.x, y: bx.y }, { x: bx.x + bx.w, y: bx.y + bx.h }, tol); },
  },
};

// Text and one-tap markers drag by their body without covering their shape with a handle.
const handlesOf = d => (d.type === 'text' || MARKERS.has(d.type) ? [] : d.pts.map((_, k) => k));

export default class DrawingEditor {
  // cb: { onChange(drawings), onSelect(selected[]), onToolChange(type|null, step), requestText(initial, done(text|null)),
  //       onStyles(styles) - the remembered per-tool settings changed (page saves them to the user's chart prefs) }
  constructor(eng, cb) {
    this.eng = eng; this.cb = cb;
    this.drawings = [];
    this.overlay = [];        // read-only drawings (other visible annotation sets): painted, never hit or saved
    this.sel = [];            // selected drawings
    this.tool = null;         // active tool type
    this.magnet = false;
    this.multi = false;       // phone "Select several" mode: taps add/remove
    this.baseStyle = { color: eng.theme.accent, width: 2, dash: 'solid', lineOpacity: 1, fillOpacity: 0.1 };
    this.styles = {};         // tool type -> last settings used: {style, fib?, alert?: {trigger, after}}
    this.act = null;          // current gesture: {kind: 'create'|'pending'|'handle'|'body', ...}
    eng.editor = this;
  }

  ctx(r, paneKey = null) {
    const eng = this.eng, pane = paneKey ? eng.paneFor(paneKey) : null;
    if (paneKey && !pane) return null;
    const range = pane ? eng.paneRange(pane.s, eng.visible()[0]) : (r || eng.yRange());
    const dp = pane?.s.key === 'volume' ? 0 : pane ? priceDp((range.max - range.min) / 10) : eng._dp || priceDp((range.max - range.min) / 10);
    return { eng, r: range, dp, P: pt => eng.ptToXY(pt, range, paneKey),
             Y: v => pane ? eng.paneY(v, pane, range) : eng.yOf(v, range),
             factor: pt => pane ? 1 : eng.ptFactor(pt),
             plotR: eng.plotR, bottom: pane ? pane.top + pane.h : eng.priceBottom,
             theme: eng.theme, g: eng.base.getContext('2d') };
  }

  paneAtPoint(p) {
    if (p.x < 0 || p.x > this.eng.plotR) return undefined;
    if (p.y >= 0 && p.y <= this.eng.priceBottom) return null;
    return this.eng.paneAt(p.y)?.s.key;
  }

  // ── Public API (used by the page / edit bar / panels) ────────────────────
  setDrawings(list) { this.drawings = list; this.select([]); this.cancelTool(); this.eng.draw(); }

  setOverlay(list) { this.overlay = list; this.eng.draw(); }

  setTool(type) {
    this.select([]);
    this.tool = type; this.act = null;
    this.cb.onToolChange(type, 0);
  }

  cancelTool() {
    if (this.act?.kind === 'pending' || this.act?.kind === 'create') this.act = null;
    this.tool = null;
    this.cb.onToolChange(null, 0);
    this.eng.draw();
  }

  select(list) { this.sel = list; this.cb.onSelect(list); this.eng.draw(); }

  // patch: {style: {...partial}} merges into each selected drawing's style; other keys
  // (text, locked, fib) are set as given.
  updateSelected(patch) {
    if (!this.sel.length) return;
    for (const d of this.sel) {
      const { style, ...rest } = patch;
      if (style) d.style = { ...d.style, ...style };
      Object.assign(d, rest);
      if (d.type === 'alert' && patch.alert) d.state = { status: 'new' };
    }
    if (patch.style || patch.fib || patch.alert) this.remember(this.sel);
    this.changed(); this.cb.onSelect(this.sel);
  }

  // The next drawing of each type starts with these drawings' current settings
  // (alert lines keep trigger + after only; direction is set from the price when drawn).
  remember(list) {
    for (const d of list) {
      this.styles[d.type] = {
        style: { ...d.style },
        ...(d.fib && { fib: { levels: d.fib.levels.map(l => ({ ...l })) } }),
        ...(d.alert && { alert: { trigger: d.alert.trigger, after: d.alert.after } }),
      };
    }
    this.cb.onStyles?.(this.styles);
  }

  newDraft(type, pt, paneKey = null) {
    const saved = this.styles[type] || {};
    const d = { key: newKey(), type, ...(paneKey && { pane: paneKey }), pts: TOOLS[type].n === 1 ? [pt] : [pt, { ...pt }],
                style: { ...this.baseStyle, ...(NO_FILL.has(type) && { fillOpacity: 0 }), ...(type === 'alert' && { color: ALERT_COLOUR }), ...(type === 'buy' && { color: this.eng.theme.up }), ...(type === 'sell' && { color: this.eng.theme.down }), ...saved.style } };
    if (CALLOUTS.has(type)) {
      d.style = { ...d.style, fillOpacity: saved.style?.fillOpacity ?? 0.9,
                  boxWidth: saved.style?.boxWidth || CALL_WIDTH,
                  textColor: saved.style?.textColor || this.eng.theme.text,
                  backgroundColor: saved.style?.backgroundColor || this.eng.theme.bg };
      delete d.style.arrowColor;
    }
    if (type === 'fib') d.fib = { levels: (saved.fib?.levels || fibLevels({})).map(l => ({ ...l })) };
    if (type === 'alert') d.alert = { ...ALERT_DEFAULT, ...saved.alert };
    return d;
  }

  // Snapshot screen-space geometry so a group pastes at the pointer on any ticker.
  copyData(list) {
    const picked = list.filter(d => d.type !== 'alert' && TOOLS[d.type] && d.pts?.length);
    if (!picked.length) return null;
    const xy = picked.map(d => d.pts.map(pt => this.eng.ptToXY(pt, undefined, d.pane)));
    if (xy.some(pts => pts.some(p => !p || !Number.isFinite(p.x) || !Number.isFinite(p.y)))) return null;
    const anchor = xy[0][0];
    return {
      anchor,
      items: picked.map((d, i) => ({
        pane: d.pane || null,
        data: JSON.parse(JSON.stringify({ type: d.type, style: d.style, text: d.text, fib: d.fib, locked: !!d.locked })),
        offsets: xy[i].map(p => ({ x: p.x - anchor.x, y: p.y - anchor.y })),
      })),
    };
  }

  copySelected() {
    const data = this.copyData(this.sel);
    if (!data) return 0;
    drawingClipboard = data;
    return data.items.length;
  }

  pasteAt(p, data = drawingClipboard) {
    if (!data || !this.eng.daily?.length || !p || !Number.isFinite(p.x) || !Number.isFinite(p.y)) return 0;
    const targetPane = this.paneAtPoint(p);
    if (targetPane === undefined) return 0;
    const panes = new Set(data.items.map(item => item.pane));
    const singlePane = panes.size === 1;
    // Mixed-pane groups retain their source pane keys; one-pane groups follow the cursor's pane.
    if (!singlePane && (targetPane !== data.items[0].pane ||
        data.items.some(item => item.pane && !this.eng.paneFor(item.pane)))) return 0;
    const added = [];
    for (const item of data.items) {
      const pane = singlePane ? targetPane : item.pane;
      const pts = item.offsets.map(o => this.eng.xyToPt(p.x + o.x, p.y + o.y, false, pane));
      if (pts.some(q => !q)) return 0;
      const d = { ...JSON.parse(JSON.stringify(item.data)), key: newKey(), pts };
      if (pane) d.pane = pane;
      added.push(d);
    }
    if (this.tool) this.cancelTool();
    this.act = null;
    this.drawings.push(...added);
    this.select(added);
    this.changed();
    return added.length;
  }

  duplicateSelected() {
    const data = this.copyData(this.sel);
    return data ? this.pasteAt({ x: data.anchor.x + 16, y: data.anchor.y + 16 }, data) : 0;
  }

  deleteSelected() {
    if (!this.sel.length) return;
    const gone = new Set(this.sel);
    this.drawings = this.drawings.filter(d => !gone.has(d));
    this.select([]); this.changed();
  }

  deleteAll() { this.drawings = []; this.select([]); this.changed(); }

  editText(d) {
    this.cb.requestText(d.text || '', text => { if (text) { d.text = text; this.changed(); this.cb.onSelect(this.sel); } });
  }

  changed() { this.eng.draw(); this.cb.onChange(this.drawings); }

  // ── Painting ──────────────────────────────────────────────────────────────
  draw(g, r, paneKey = null) {
    const c = this.ctx(r, paneKey);
    if (!c) return;
    const inPane = d => (d.pane || null) === paneKey;
    const all = [...this.overlay, ...this.drawings, ...(this.act?.draft ? [this.act.draft] : [])].filter(inPane);
    for (const d of all) {
      g.save();
      g.strokeStyle = d.style.color; g.lineWidth = d.style.width;
      g.globalAlpha = lineA(d);
      g.setLineDash(DASH[d.style.dash] || []);
      TOOLS[d.type].paint(g, d, c);
      g.restore();
    }
    const shown = (this.act?.draft ? [this.act.draft] : this.sel).filter(inPane);
    const rad = this.coarse ? 8 : 5.5;
    for (const s of shown) {
      for (const k of handlesOf(s)) {
        const p = c.P(s.pts[k]);
        g.beginPath(); g.arc(p.x, p.y, rad, 0, Math.PI * 2);
        g.lineWidth = 2; g.strokeStyle = s.locked ? this.eng.theme.muted : s.style.color; g.stroke();
      }
      if (CALLOUTS.has(s.type)) {
        const b = calloutBox(g, s, c);
        g.save(); g.strokeStyle = s.style.color; g.lineWidth = 2; g.setLineDash([]);
        g.strokeRect(b.x + b.w - rad, b.y + b.h - rad, rad * 2, rad * 2);
        g.restore();
      }
      if (s.type === 'text' && this.sel.includes(s)) {      // text has no handles: dashed outline instead
        const bx = TOOLS.text.box(g, s, c);
        g.save(); g.setLineDash([3, 3]); g.strokeStyle = s.style.color; g.lineWidth = 1;
        g.strokeRect(bx.x - 2, bx.y - 2, bx.w + 4, bx.h + 4); g.restore();
      }
    }
  }

  // ── Gestures (called from gestures.js; return true when the editor takes the gesture)
  hitTest(p, tol) {
    const paneKey = this.paneAtPoint(p);
    if (paneKey === undefined) return null;
    const c = this.ctx(undefined, paneKey);
    if (!c) return null;
    for (let i = this.drawings.length - 1; i >= 0; i--) {
      const d = this.drawings[i];
      if ((d.pane || null) === paneKey && TOOLS[d.type].hit(d, c, p, tol)) return d;
    }
    return null;
  }

  down(p, ptype, additive = false) {
    this.coarse = ptype !== 'mouse';
    const tol = this.coarse ? 14 : 6, grab = this.coarse ? 22 : 9;
    const paneKey = this.paneAtPoint(p);
    if (paneKey === undefined) return false;
    const pt = this.eng.xyToPt(p.x, p.y, this.magnet, paneKey);
    if (!pt) return false;

    if (this.tool) {
      const T = TOOLS[this.tool];
      if (this.act?.kind === 'pending' || (paneKey && this.tool === 'alert')) return false;
      const draft = this.newDraft(this.tool, pt, paneKey);
      if (T.n === 1) {
        if (draft.type === 'text') {
          this.tool = null; this.cb.onToolChange(null, 0);
          this.cb.requestText('', text => { if (text) { draft.text = text; this.finish(draft); } });
        } else {
          this.finish(draft);
          this.act = { kind: 'handle', d: draft, k: 0, moved: false };   // keep holding to slide it into place
        }
      } else {
        this.act = { kind: 'create', draft, start: p, moved: false };
        this.cb.onToolChange(this.tool, 1);
        this.eng.draw();
      }
      return true;
    }

    const add = additive || this.multi;
    if (!add) {
      const c = this.ctx(undefined, paneKey);
      for (const s of this.sel) {
        if ((s.pane || null) !== paneKey) continue;
        if (CALLOUTS.has(s.type)) {
          const b = calloutBox(c.g, s, c);
          if (Math.hypot(p.x - b.x - b.w, p.y - b.y - b.h) <= grab) {
            if (!s.locked) this.act = { kind: 'resize', d: s, start: p, w: b.w, h: b.h, moved: false };
            return true;
          }
        }
        for (const k of handlesOf(s)) {
          const h = c.P(s.pts[k]);
          if (Math.hypot(h.x - p.x, h.y - p.y) <= grab) {
            if (!s.locked) this.act = { kind: 'handle', d: s, k, moved: false };
            return true;
          }
        }
      }
    }
    const hit = this.hitTest(p, tol);
    if (hit) {
      if (add) {
        if (this.sel.includes(hit)) { this.select(this.sel.filter(d => d !== hit)); return true; }
        this.select([...this.sel, hit]);
      } else if (!this.sel.includes(hit)) this.select([hit]);
      // Dragging callout text moves its box, not the point it identifies.
      if (!add && this.sel.length === 1 && CALLOUTS.has(hit.type) && !hit.locked) {
        const c = this.ctx(undefined, paneKey), b = calloutBox(c.g, hit, c);
        if (inBox(p, { x: b.x, y: b.y }, { x: b.x + b.w, y: b.y + b.h }, 0)) {
          this.act = { kind: 'handle', d: hit, k: 1, moved: false };
          return true;
        }
      }
      // Drag moves every selected, unlocked drawing together.
      const movers = this.sel.filter(d => !d.locked && (!d.pane || this.eng.paneFor(d.pane)));
      if (movers.length) this.act = { kind: 'body', start: p, moved: false, items: movers.map(d => ({ d, orig: d.pts.map(q => this.eng.ptToXY(q, undefined, d.pane)) })) };
      return true;
    }
    if (this.sel.length && !add) this.select([]);
    return false;
  }

  move(p) {
    const a = this.act;
    if (!a) return;
    if (a.kind === 'create') {
      if (Math.hypot(p.x - a.start.x, p.y - a.start.y) > 6) a.moved = true;
      a.draft.pts[1] = this.eng.xyToPt(p.x, p.y, this.magnet, a.draft.pane) || a.draft.pts[1];
    } else if (a.kind === 'handle') {
      a.d.pts[a.k] = this.eng.xyToPt(p.x, p.y, this.magnet, a.d.pane) || a.d.pts[a.k];
      a.moved = true;
    } else if (a.kind === 'resize') {
      a.d.style.boxWidth = Math.round(Math.max(92, a.w + p.x - a.start.x));
      a.d.style.boxHeight = Math.round(Math.max(CALL_HEIGHT, a.h + p.y - a.start.y));
      a.moved = true;
    } else if (a.kind === 'body') {
      const dx = p.x - a.start.x, dy = p.y - a.start.y;
      if (!a.moved && Math.hypot(dx, dy) < 4) return;
      a.moved = true;
      for (const { d, orig } of a.items) d.pts = orig.map((o, k) => this.eng.xyToPt(o.x + dx, o.y + dy, false, d.pane) || d.pts[k]);
    }
    this.eng.draw();
  }

  up() {
    const a = this.act;
    if (!a) return;
    if (a.kind === 'create') {
      if (a.moved) {
        if (a.draft.type === 'channel') this.awaitChannelWidth(a.draft);
        else this.finish(a.draft);
      } else { a.kind = 'pending'; a.step = 1; this.cb.onToolChange(this.tool, 1); }   // wait for point B
      return;
    }
    this.act = null;
    if (a.moved) {
      for (const d of a.kind === 'body' ? a.items.map(x => x.d) : [a.d]) if (d.type === 'alert') d.state = { status: 'new' };
      if (a.kind === 'resize') this.remember([a.d]);
      this.changed();
    }
  }

  // A second finger arrived mid-gesture (pinch): drop the half-made drawing / finish the drag.
  cancelGesture() {
    const a = this.act;
    if (!a) return;
    if (a.kind === 'create') { this.act = null; this.cb.onToolChange(this.tool, 0); this.eng.draw(); return; }
    if (a.kind === 'pending') return;
    this.act = null;
    if (a.moved) this.changed();
  }

  // Desktop: while waiting for the next anchor, it follows the mouse.
  hover(p) {
    if (this.act?.kind !== 'pending') return false;
    const k = this.act.step || 1;
    const d = this.act.draft;
    if (this.paneAtPoint(p) !== (d.pane || null)) return true;
    d.pts[k] = this.eng.xyToPt(p.x, p.y, this.magnet, d.pane) || d.pts[k];
    this.eng.draw();
    return true;
  }

  // While an anchor tap is pending, drags pan the chart instead of drawing.
  get pending() { return this.act?.kind === 'pending'; }

  awaitChannelWidth(d) {
    if (d.pts.length < 3) d.pts.push({ ...d.pts[0] });
    this.act = { kind: 'pending', draft: d, step: 2 };
    this.cb.onToolChange(this.tool, 2);
    this.eng.draw();
  }

  // Clean tap/click while pending: set the next anchor.
  tapB(p) {
    if (!this.pending) return false;
    const k = this.act.step || 1, d = this.act.draft;
    if (this.paneAtPoint(p) !== (d.pane || null)) return false;
    d.pts[k] = this.eng.xyToPt(p.x, p.y, this.magnet, d.pane) || d.pts[k];
    if (d.type === 'channel' && k === 1) this.awaitChannelWidth(d);
    else this.finish(d);
    return true;
  }

  finish(d) {
    if (CALLOUTS.has(d.type) && !d.text) {
      this.act = null;
      this.tool = null;
      this.cb.onToolChange(null, 0);
      this.eng.draw();
      this.cb.requestText('', text => { if (text) { d.text = text; this.finish(d); } });
      return;
    }
    if (d.type === 'arc' && d.pts.length === 2) {        // add the bulge handle H
      const [a, b] = d.pts.map(q => this.eng.ptToXY(q, undefined, d.pane)), h = arcDefaultH(a, b);
      d.pts.push(this.eng.xyToPt(h.x, h.y, false, d.pane) || { ...d.pts[1] });
    }
    if (d.type === 'alert') {        // waiting for a break up if the line is above today's close, else down
      const v = alertValueNow(d, this.eng), last = this.eng.daily?.[this.eng.daily.length - 1];
      if (v != null && last) d.alert.dir = v >= last.c ? 'above' : 'below';
      d.state = { status: 'new' };
    }
    this.drawings.push(d);
    this.act = null;
    this.tool = null;
    this.cb.onToolChange(null, 0);
    this.select([d]);
    this.changed();
  }
}
